import { createFileRoute, Link } from '@tanstack/react-router'
import { getBreaches } from '@/hooks/useBreaches'
import DOMPurify from 'dompurify'
import { FiArrowLeft } from 'react-icons/fi'
import { CheckCircle2, AlertTriangle, ShieldAlert, Bug, FileWarning, Eye } from 'lucide-react'
import { BrandedSpinner } from '@/components/BrandedSpinner'

export const Route = createFileRoute('/_auth/data-breaches/$breachId')({
  component: BreachDetail,
  loader: async ({ params }) => {
    return { breachId: params.breachId }
  },
})

function addTargetBlankToLinks(html: string) {
  const doc = document.createElement('div');
  doc.innerHTML = html;
  doc.querySelectorAll('a').forEach(a => {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
  });
  return doc.innerHTML;
}

function formatDate(dateString: string) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/* ─── Sub-components ──────────────────────────────────────────────── */

function UserInfoCard({ userInfo }: { userInfo: string[] }) {
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100">
          <Eye className="h-5 w-5 text-amber-600" />
        </div>
        <h2 className="text-lg font-semibold text-amber-800">Your Data Found</h2>
      </div>
      <div className="flex flex-wrap gap-2">
        {userInfo.map((info, i) => (
          <span
            key={i}
            className="inline-block rounded-full border border-amber-200 bg-amber-100/70 px-3 py-1 text-sm font-medium text-amber-800"
          >
            {info}
          </span>
        ))}
      </div>
    </div>
  )
}

function CompromisedDataCard({ dataClasses, userInfo }: { dataClasses: string[]; userInfo?: string[] }) {
  const userInfoSet = new Set((userInfo || []).map(u => u.toLowerCase()))
  const recMap: Record<string, string> = {
    'Email addresses': 'Change your password and enable 2FA on accounts using this email.',
    'Passwords': 'Change your password immediately and avoid reusing old passwords.',
    'Usernames': 'Check if this username is used elsewhere and monitor for suspicious logins.',
    'Phone numbers': 'Be cautious of phishing texts or calls.',
    'Physical addresses': 'Watch for suspicious mail or packages.',
    'Credit card numbers': 'Contact your bank and monitor transactions.',
    'IP addresses': 'Be aware of potential targeted attacks.',
  }

  return (
    <div className="rounded-2xl border border-red-200 bg-red-50/60 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-100">
          <ShieldAlert className="h-5 w-5 text-red-600" />
        </div>
        <h2 className="text-lg font-semibold text-red-800">Compromised Data</h2>
      </div>
      <div className="space-y-2">
        {dataClasses.map((dc, i) => {
          const isUser = userInfoSet.has(dc.toLowerCase())
          return (
            <div
              key={i}
              className={`rounded-xl px-4 py-3 text-sm ${
                isUser
                  ? 'border border-red-200 bg-red-100/60'
                  : 'border border-red-100 bg-white/50'
              }`}
            >
              <span className={`font-medium ${isUser ? 'text-red-800' : 'text-red-700'}`}>{dc}</span>
              {isUser && recMap[dc] && (
                <p className="mt-1 text-xs text-red-600 leading-relaxed">{recMap[dc]}</p>
              )}
            </div>
          )
        })}
      </div>
      <p className="mt-4 text-xs text-slate-500">
        Highlighted items match your data found in this breach. Take action on those first.
      </p>
    </div>
  )
}

function Recommendations({ type }: { type: string }) {
  const recommendationsMap: Record<string, string[]> = {
    'Database Stolen': [
      'Change your password immediately for any accounts using the same email.',
      'Enable two-factor authentication (2FA) wherever available.',
      'Monitor your accounts for any suspicious activity.',
      'Be cautious of phishing emails that reference this breach.'
    ],
    'Accounts Compromised': [
      'Reset your password and stop reusing old passwords.',
      'Check your other accounts for similar credentials.',
      'Enable two-factor authentication (2FA) on all important accounts.',
      'Watch for unauthorized logins or account changes.'
    ],
    'Payment Details Compromised': [
      'Contact your bank or card provider to monitor or freeze your card.',
      'Review recent transactions for suspicious activity.',
      'Consider placing a fraud alert on your credit file.',
      'Change passwords for any affected accounts.'
    ],
  }
  const recs = recommendationsMap[type] || [
    'Review your account security settings.',
    'Change your password as a precaution.',
    'Monitor for suspicious activity.'
  ]

  return (
    <div className="rounded-2xl border border-violet-200 bg-violet-50/60 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100">
          <CheckCircle2 className="h-5 w-5 text-violet-600" />
        </div>
        <h2 className="text-lg font-semibold text-violet-800">Recommendations</h2>
      </div>
      <div className="space-y-2">
        {recs.map((rec, i) => (
          <div key={i} className="flex items-start gap-3 rounded-xl border border-violet-100 bg-white/50 px-4 py-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700">
              {i + 1}
            </span>
            <p className="text-sm text-violet-900 leading-relaxed">{rec}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ─── Breach flags (only show true/relevant flags) ────────────────── */

function BreachFlags({ breach }: { breach: { IsFabricated: boolean; IsSensitive: boolean; IsRetired: boolean; IsSpamList: boolean; IsMalware: boolean; IsStealerLog: boolean } }) {
  const flags: { key: string; label: string; show: boolean; color: string; icon: React.ReactNode }[] = [
    { key: 'fabricated', label: 'Possibly Fabricated', show: breach.IsFabricated, color: 'bg-amber-100 text-amber-700 border-amber-200', icon: <FileWarning className="h-3.5 w-3.5" /> },
    { key: 'sensitive', label: 'Sensitive', show: breach.IsSensitive, color: 'bg-rose-100 text-rose-700 border-rose-200', icon: <AlertTriangle className="h-3.5 w-3.5" /> },
    { key: 'retired', label: 'Retired', show: breach.IsRetired, color: 'bg-slate-100 text-slate-600 border-slate-200', icon: <AlertTriangle className="h-3.5 w-3.5" /> },
    { key: 'spamlist', label: 'Spam List', show: breach.IsSpamList, color: 'bg-orange-100 text-orange-700 border-orange-200', icon: <AlertTriangle className="h-3.5 w-3.5" /> },
    { key: 'malware', label: 'Malware Related', show: breach.IsMalware, color: 'bg-red-100 text-red-700 border-red-200', icon: <Bug className="h-3.5 w-3.5" /> },
    { key: 'stealerlog', label: 'Stealer Log', show: breach.IsStealerLog, color: 'bg-red-100 text-red-700 border-red-200', icon: <ShieldAlert className="h-3.5 w-3.5" /> },
  ]

  const activeFlags = flags.filter(f => f.show)
  if (activeFlags.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2">
      {activeFlags.map(f => (
        <span
          key={f.key}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${f.color}`}
        >
          {f.icon}
          {f.label}
        </span>
      ))}
    </div>
  )
}

/* ─── Main component ──────────────────────────────────────────────── */

function BreachDetail() {
  const { breachId } = Route.useLoaderData()
  const { data: breaches, isLoading, isError } = getBreaches()
  const search = Route.useSearch();
  const breach = breaches?.find(b => b.id === breachId)

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <BrandedSpinner size="xl" />
          <p className="mt-4 text-base text-slate-500">Loading breach details...</p>
        </div>
      </div>
    )
  }

  // ── Error ──
  if (isError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="rounded-2xl border border-red-200 bg-red-50 px-8 py-10 shadow-sm">
            <AlertTriangle className="mx-auto h-10 w-10 text-red-400" />
            <p className="mt-3 text-lg font-semibold text-red-700">Something went wrong</p>
            <p className="mt-1 text-sm text-red-600">We couldn't load breach details. Please try again later.</p>
          </div>
          <Link
            to="/footprint"
            search={{ invitationcode: search.invitationcode, __clerk_ticket: undefined }}
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-violet-600 hover:text-violet-700"
          >
            <FiArrowLeft className="h-4 w-4" />
            Back to Breach Monitor
          </Link>
        </div>
      </div>
    )
  }

  // ── Not found ──
  if (!breach) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="rounded-2xl border border-slate-200/80 bg-white/70 px-8 py-10 shadow-sm">
            <AlertTriangle className="mx-auto h-10 w-10 text-slate-400" />
            <p className="mt-3 text-lg font-semibold text-slate-700">Breach not found</p>
            <p className="mt-1 text-sm text-slate-500">This breach may have been removed or doesn't exist.</p>
          </div>
          <Link
            to="/footprint"
            search={{ invitationcode: search.invitationcode, __clerk_ticket: undefined }}
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-violet-600 hover:text-violet-700"
          >
            <FiArrowLeft className="h-4 w-4" />
            Back to Breach Monitor
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-16 space-y-6">
        {/* Back navigation */}
        <Link
          to="/footprint"
          search={{ invitationcode: search.invitationcode, __clerk_ticket: undefined }}
          className="inline-flex items-center gap-2 text-base text-slate-500 transition-colors hover:text-slate-700"
        >
          <FiArrowLeft className="h-4 w-4" />
          Back to Breach Monitor
        </Link>

        {/* Header card */}
        <div className="rounded-3xl border border-white/70 bg-white/70 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.08)] p-6 sm:p-8">
          <div className="flex items-start gap-5">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white">
              {breach.LogoPath ? (
                <img
                  src={breach.LogoPath}
                  alt={breach.Name + ' logo'}
                  className="h-full w-full object-contain p-2"
                  onError={e => { e.currentTarget.style.display = 'none'; }}
                />
              ) : (
                <span className="text-2xl font-bold text-violet-500">{breach.Name[0]}</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={
                    `inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ` +
                    (breach.Type === 'Database Stolen' ? 'bg-violet-100 text-violet-700' :
                     breach.Type === 'Accounts Compromised' ? 'bg-red-100 text-red-700' :
                     'bg-slate-100 text-slate-600')
                  }
                >
                  {breach.Type}
                </span>
                {breach.IsVerified && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700">
                    <CheckCircle2 className="h-3 w-3" />
                    Verified
                  </span>
                )}
              </div>
              <h1 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">{breach.Title}</h1>
              {breach.Domain && <p className="mt-1 text-sm text-slate-500">{breach.Domain}</p>}
            </div>
          </div>

          {/* Metadata grid */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-slate-100 pt-5">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400">Breach Date</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{breach.BreachDate}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400">Accounts Affected</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{breach.PwnCount.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400">Added</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{formatDate(breach.AddedDate)}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400">Last Modified</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{formatDate(breach.ModifiedDate)}</p>
            </div>
          </div>

          {/* Breach flags (only true ones) */}
          <div className="mt-5">
            <BreachFlags breach={breach} />
          </div>
        </div>

        {/* Description */}
        <div className="rounded-2xl border border-slate-200/80 bg-white/70 shadow-sm p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-3">About This Breach</h2>
          <div
            className="text-sm text-slate-700 leading-relaxed breach-description"
            dangerouslySetInnerHTML={{ __html: addTargetBlankToLinks(DOMPurify.sanitize(breach.Description)) }}
          />
        </div>

        {/* User info (only if data present) */}
        {breach.userInfo && breach.userInfo.length > 0 && (
          <UserInfoCard userInfo={breach.userInfo} />
        )}

        {/* Compromised data */}
        <CompromisedDataCard dataClasses={breach.DataClasses} userInfo={breach.userInfo} />

        {/* Recommendations */}
        <Recommendations type={breach.Type} />
      </div>
    </div>
  )
}
